import { useState } from "react";
import { Badge, Button, Modal, Label } from "flowbite-react";
import { HiBan, HiKey, HiPencil, HiRefresh, HiTrash, HiUserGroup, HiPlus } from "react-icons/hi";
import { Client, ClientGroup, DataGridPagination } from "@/sharedTypes";
import DataTable from "@/components/Table";
import ClientFormDialog from "@/components/clients/ClientFormDialog";
import ClientGroupFormDialog from "@/components/clientGroups/ClientGroupFormDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import {
  useDeleteClientMutation,
  useEditClientMutation,
  useHardDeleteClientMutation,
  useRestoreClientMutation,
  useUpdateClientPasswordMutation,
} from "@/api/clientApi/client";
import ClientPasswordDialog from "@/components/clients/ClientPasswordDialog";
import {
  useGetClientGroupsQuery,
  useAssignClientToGroupMutation,
  useCreateClientGroupMutation,
} from "@/api/clientGroupApi/clientGroup";
import Select from "react-select";
import { toast } from "react-toastify";
import { getErrorMessage } from "@/utils/requestUtils";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";

interface ClientListTableProps {
  data: Client[];
  pagination: DataGridPagination;
  onPageChange: (page: number) => void;
  showInactive: boolean;
}

export default function ClientListTable({
  data,
  pagination,
  onPageChange,
  showInactive,
}: ClientListTableProps) {
  const { t } = useTranslation(["client", "common", "clientGroup"]);
  const user = useCurrentUser();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | undefined>();

  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    body: string;
    action: "disable" | "restore" | "hardDelete";
    onConfirm: () => void;
  } | null>(null);

  const [assignGroupClient, setAssignGroupClient] = useState<Client | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<{ label: string; value: number } | null>(null);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [clientToUpdatePassword, setClientToUpdatePassword] = useState<number | null>(null);

  const [editClient] = useEditClientMutation();
  const [deleteClient, { isLoading: isDeleting }] = useDeleteClientMutation();
  const [restoreClient, { isLoading: isRestoring }] = useRestoreClientMutation();
  const [hardDeleteClient, { isLoading: isHardDeleting }] =
    useHardDeleteClientMutation();
  const [assignClientToGroup] = useAssignClientToGroupMutation();
  const [createClientGroup] = useCreateClientGroupMutation();
  const [updateClientPassword, { isLoading: isUpdatingPassword }] = useUpdateClientPasswordMutation();

  const { data: clientGroupsData } = useGetClientGroupsQuery({
    pagination: { page: 1, pageSize: 100 },
    filters: { is_active: true },
  });

  const groupOptions = (clientGroupsData?.items || []).map((g: any) => ({
    label: g.group_name,
    value: g.client_group_id,
  }));

  const handleAssignToGroup = () => {
    if (!assignGroupClient || !selectedGroupId) return;
    assignClientToGroup({
      clientGroupId: selectedGroupId.value,
      client_ids: [assignGroupClient.client_id],
    })
      .unwrap()
      .then(() => {
        toast.success(t("client:assignToGroupSuccess"));
        setAssignGroupClient(null);
        setSelectedGroupId(null);
      })
      .catch((err) => toast.error(getErrorMessage(err) || t("client:assignToGroupFail")));
  };

  const handleCreateGroupSubmit = async (data: ClientGroup) => {
    try {
      await createClientGroup(data).unwrap();
      toast.success(t("clientGroup:groupCreated"));
      setIsCreateGroupOpen(false);
    } catch (error) {
      toast.error(t("clientGroup:createFailed"));
    }
  };

  const handlePasswordClick = (clientId: number) => {
    setClientToUpdatePassword(clientId);
    setIsPasswordModalOpen(true);
  };

  const handlePasswordSubmit = (password: string) => {
    if (!clientToUpdatePassword) return;
    updateClientPassword({ clientId: clientToUpdatePassword, password })
      .unwrap()
      .then(() => {
        toast.success(t("client:passwordUpdateSuccess"));
        setIsPasswordModalOpen(false);
        setClientToUpdatePassword(null);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleEditClick = (client: Client) => {
    setSelectedClient(client);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = (formData: Client) => {
    editClient({ ...formData, client_id: selectedClient?.client_id || formData.client_id })
      .unwrap()
      .then(() => {
        toast.success(t("client:updateSuccess"));
        setIsEditModalOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleDisable = (id: number) => {
    setConfirmDialog({
      title: t("client:disableConfirmTitle"),
      body: t("client:disableConfirmBody"),
      action: "disable",
      onConfirm: () => {
        deleteClient(id)
          .unwrap()
          .then(() => {
            toast.success(t("client:disableSuccess"));
            setConfirmDialog(null);
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const handleRestore = (id: number) => {
    setConfirmDialog({
      title: t("client:restoreConfirmTitle"),
      body: t("client:restoreConfirmBody"),
      action: "restore",
      onConfirm: () => {
        restoreClient(id)
          .unwrap()
          .then(() => {
            toast.success(t("client:restoreSuccess"));
            setConfirmDialog(null);
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const handleHardDelete = (id: number) => {
    setConfirmDialog({
      title: t("client:deleteConfirmTitle"),
      body: t("client:deleteConfirmBody"),
      action: "hardDelete",
      onConfirm: () => {
        hardDeleteClient(id)
          .unwrap()
          .then(() => {
            toast.success(t("client:deleteSuccess"));
            setConfirmDialog(null);
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const formatDate = (value?: string) => {
    if (!value) return "-";
    // Backend sends UTC (ISO with "Z"); local getters render in the browser's
    // timezone automatically.
    const d = new Date(value);
    if (isNaN(d.getTime())) return "-";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const columns = [
    { key: "company_id", label: t("client:companyId") },
    {
      key: "client_username",
      label: t("client:clientDisplayName"),
      render: (row: Client) => (
        <span>{row.client_displayname || row.client_username}</span>
      ),
    },
    { key: "client_email", label: t("client:clientEmail") },
    {
      key: "createdAt",
      label: t("client:registeredAt"),
      render: (row: Client) => <span>{formatDate(row.createdAt)}</span>,
    },
    {
      key: "client_group",
      label: t("client:clientGroup"),
      render: (row: Client) => (
        row.client_group ? (
          <Badge color="info" className="w-fit">
            {row.client_group.group_name}
          </Badge>
        ) : (
          <span className="text-gray-400 text-sm">{t("client:noGroup")}</span>
        )
      ),
    },
    {
      key: "actions",
      label: t("client:actions"),
      render: (row: Client) => (
        <div className="flex gap-2">
          {!showInactive ? (
            <>
              {user?.agent_role !== agentRoles.agent && user?.agent_role !== agentRoles.super_admin && (
                <>
                  <Button size="xs" color="light" onClick={() => handleEditClick(row)}>
                    <HiPencil className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  </Button>
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => handlePasswordClick(row.client_id)}
                    title={t("client:setPassword")}
                  >
                    <HiKey className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </Button>
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => setAssignGroupClient(row)}
                    title={t("client:assignToGroup")}
                  >
                    <HiUserGroup className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
                  </Button>
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => handleDisable(row.client_id)}
                  >
                    <HiBan className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              <Button
                size="xs"
                color="light"
                onClick={() => handleRestore(row.client_id)}
              >
                <HiRefresh className="h-4 w-4 text-green-600 dark:text-green-400" />
              </Button>
              <Button
                size="xs"
                color="light"
                onClick={() => handleHardDelete(row.client_id)}
              >
                <HiTrash className="h-4 w-4 text-red-600 dark:text-red-400" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        pagination={pagination}
        onPageChange={onPageChange}
      />

      {isEditModalOpen && (
        <ClientFormDialog
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleEditSubmit}
          mode="edit"
          initialData={selectedClient}
        />
      )}

      {confirmDialog && (
        <ConfirmationDialog
          title={confirmDialog.title}
          body={confirmDialog.body}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmDialog.onConfirm}
          isLoading={
            confirmDialog.action === "disable"
              ? isDeleting
              : confirmDialog.action === "restore"
                ? isRestoring
                : isHardDeleting
          }
        />
      )}

      <Modal
        show={!!assignGroupClient}
        onClose={() => { setAssignGroupClient(null); setSelectedGroupId(null); }}
      >
        <Modal.Header>{t("client:assignToGroupTitle")}</Modal.Header>
        <Modal.Body className="overflow-visible">
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <Label value={t("client:selectGroup")} />
              <Button
                size="xs"
                color="light"
                onClick={() => setIsCreateGroupOpen(true)}
              >
                <HiPlus className="mr-1 h-3 w-3" />
                {t("client:createNewGroup")}
              </Button>
            </div>
            <Select
              options={groupOptions}
              value={selectedGroupId}
              onChange={(selected) => setSelectedGroupId(selected as any)}
              placeholder={t("client:selectGroup")}
              className="basic-select"
              classNamePrefix="select"
              isClearable
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => { setAssignGroupClient(null); setSelectedGroupId(null); }}>
            {t("common:cancel")}
          </Button>
          <Button onClick={handleAssignToGroup} disabled={!selectedGroupId}>
            {t("client:assignToGroup")}
          </Button>
        </Modal.Footer>
      </Modal>

      <ClientGroupFormDialog
        open={isCreateGroupOpen}
        onClose={() => setIsCreateGroupOpen(false)}
        onSubmit={handleCreateGroupSubmit}
        mode="create"
      />

      <ClientPasswordDialog
        open={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false);
          setClientToUpdatePassword(null);
        }}
        onSubmit={handlePasswordSubmit}
        isLoading={isUpdatingPassword}
      />
    </>
  );
}
